import {I18nKeys} from '@multipass-i18n';
import {Button, CodeInput, FieldError, FieldHelp, FieldLabel} from '@multipass-ui-components';
import {usePublishEvent} from 'esp-js-react';
import {ArrowLeft} from 'lucide-react';
import * as React from 'react';

import {
    LoginLinkRequestedEvent,
    SESSION_STORE_ID,
    SessionEvents,
    SessionStoreModel,
    useT,
    useTReason,
    VerifyCodeRequestedEvent,
} from '../../logic';
import {AuthHead, AuthSwitchRow} from './AuthLayout';

const linkButtonClass = 'border-0 bg-transparent p-0 font-bold text-link';

const ResendRow: React.FunctionComponent<{onResend: () => void}> = ({onResend}: {onResend: () => void}) => {
    const t = useT();
    // The cooldown is view-local UI ephemera, not session state — it resets with the view.
    const [cooldownSeconds, setCooldownSeconds] = React.useState(30);
    const coolingDown = cooldownSeconds > 0;
    React.useEffect(() => {
        if (!coolingDown) {
            return undefined;
        }
        const timer = setInterval(() => setCooldownSeconds(s => Math.max(0, s - 1)), 1000);
        return () => clearInterval(timer);
    }, [coolingDown]);
    const onClick = () => {
        onResend();
        setCooldownSeconds(30);
    };
    return (
        <p className="mt-4 flex items-center justify-center gap-1.5 text-label text-text-secondary">
            {t(I18nKeys.enterCode.resendPrompt)}{' '}
            <button
                type="button"
                className={`${linkButtonClass} disabled:cursor-not-allowed disabled:text-text-muted`}
                disabled={cooldownSeconds > 0}
                onClick={onClick}
            >
                {cooldownSeconds > 0 ? t(I18nKeys.enterCode.resendCooldown, {seconds: cooldownSeconds}) : t(I18nKeys.enterCode.resend)}
            </button>
        </p>
    );
};

export const EnterCodeView: React.FunctionComponent<{session: SessionStoreModel}> = ({session}: {session: SessionStoreModel}) => {
    const [code, setCode] = React.useState('');
    const publishEvent = usePublishEvent();
    const t = useT();
    const tReason = useTReason();
    const isVerifying = session.status === 'loggingIn';
    const hasError = session.status === 'loginFailed';
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isVerifying) {
            return;
        }
        publishEvent(SESSION_STORE_ID, SessionEvents.verifyCodeRequested, {code} satisfies VerifyCodeRequestedEvent);
    };
    const onResend = () => {
        publishEvent(SESSION_STORE_ID, SessionEvents.loginLinkRequested, {email: session.pendingEmail!} satisfies LoginLinkRequestedEvent);
    };
    const onUseDifferentEmail = () => {
        publishEvent(SESSION_STORE_ID, SessionEvents.loginRestartRequested, {});
    };
    return (
        <>
            <AuthHead
                title={t(I18nKeys.enterCode.title)}
                sub={t(I18nKeys.enterCode.subtitle)}
                sentTo={session.pendingEmail ?? undefined}
            />
            <form onSubmit={onSubmit} noValidate>
                <div className="mb-5">
                    <FieldLabel htmlFor="code-digit-1">{t(I18nKeys.enterCode.codeLabel)}</FieldLabel>
                    <CodeInput
                        value={code}
                        onChange={setCode}
                        ariaLabel={t(I18nKeys.enterCode.codeInputAriaLabel)}
                        disabled={isVerifying}
                        invalid={hasError}
                        className="mt-2"
                    />
                    {hasError ? (
                        <FieldError>{t(I18nKeys.enterCode.loginFailed, {error: tReason(session.errorCode, session.error ?? '')})}</FieldError>
                    ) : (
                        <FieldHelp>{t(I18nKeys.enterCode.codeExpiry, {minutes: 15})}</FieldHelp>
                    )}
                </div>
                <Button type="submit" size="lg" block disabled={isVerifying}>
                    {isVerifying ? t(I18nKeys.enterCode.verifying) : t(I18nKeys.enterCode.verify)}
                </Button>
                <ResendRow onResend={onResend}/>
            </form>
            <AuthSwitchRow>
                <button type="button" className={`inline-flex items-center gap-1.5 ${linkButtonClass}`} onClick={onUseDifferentEmail}>
                    <ArrowLeft size={16}/>
                    {t(I18nKeys.enterCode.switchLink)}
                </button>
            </AuthSwitchRow>
        </>
    );
};
